import type { LaunchHandoffMode } from "@/lib/launch/launchHandoff";

/**
 * Launch continuity phases for project-home → workbench navigation.
 *
 * Orchestration (separate from hub chrome / conversation search):
 * 1. {@link ProjectHomeIntentField} stashes the first message in
 *    `usePendingConversationPromptStore` and starts handoff in `useLaunchHandoffStore`.
 * 2. {@link completeLaunchAfterNavigate} finishes file uploads after route change.
 * 3. {@link PendingPromptDrainer} appends the stash via Assistant UI once messages load
 *    and {@link canDrainDuringLaunchHandoff} allows it; handoff finalizes when SSE starts
 *    ({@link completeLaunchDrainHandoff}).
 * 4. {@link LaunchHandoffFlight} renders the motion surrogate until `ready`.
 */
export type LaunchHandoffPhase =
  | "idle"
  | "preparing"
  | "navigating"
  | "flying"
  | "settling"
  | "ready";

export function isLaunchHandoffActive(phase: LaunchHandoffPhase): boolean {
  return phase !== "idle" && phase !== "ready";
}

/** True when the prompt drainer may append the stashed first message. */
export function isLaunchHandoffReadyForDrain(
  phase: LaunchHandoffPhase,
  conversationId: string | null,
  targetConversationId: string,
): boolean {
  if (conversationId !== targetConversationId) return false;
  return canDrainDuringLaunchHandoff(phase, conversationId, targetConversationId, "motion");
}

/**
 * Whether the workbench may drain the stashed launch prompt for the target conversation.
 * Motion phases are cosmetic; drain is not blocked on flight completion.
 */
export function canDrainDuringLaunchHandoff(
  phase: LaunchHandoffPhase,
  handoffConversationId: string | null,
  targetConversationId: string,
  mode: LaunchHandoffMode,
): boolean {
  if (handoffConversationId !== targetConversationId) return true;
  if (phase === "idle" || phase === "ready") return true;
  if (mode === "none") return true;
  return phase === "navigating" || phase === "flying" || phase === "settling";
}

export function canTransitionLaunchPhase(
  from: LaunchHandoffPhase,
  to: LaunchHandoffPhase,
): boolean {
  if (from === to) return true;
  const allowed: Record<LaunchHandoffPhase, LaunchHandoffPhase[]> = {
    idle: ["preparing"],
    preparing: ["navigating", "idle"],
    navigating: ["flying", "ready", "idle"],
    flying: ["settling", "ready", "idle"],
    settling: ["ready", "idle"],
    ready: ["idle"],
  };
  return allowed[from]?.includes(to) ?? false;
}
